import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './styles.css';

/**
 * BridgeDeleteMenu
 *
 * Compact confirmation popover shown on the canvas when deleting node(s)
 * that sit between upstream and downstream connections. Lets the user
 * bridge the connections (upstream wired directly to downstream, with
 * AND/OR conditions ported over) or just delete and break them.
 *
 * Keyboard: Enter = bridge, Delete/Backspace = just delete, Esc = cancel.
 */
const BridgeDeleteMenu = ({
  position, // { x, y } screen coordinates (anchor: node center)
  taskCount = 1,
  upstreamCount = 0,
  downstreamCount = 0,
  onBridge,
  onBreak,
  onCancel,
}) => {
  const menuRef = useRef(null);

  useEffect(() => {
    const handlePointerDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onCancel();
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        onBridge();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        e.stopPropagation();
        onBreak();
      }
    };

    // Capture phase so the flowchart's own Delete/Escape handlers don't fire
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handlePointerDown, true);
      window.addEventListener('keydown', handleKeyDown, true);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handlePointerDown, true);
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [onCancel, onBridge, onBreak]);

  // Close if the canvas starts panning underneath us
  useEffect(() => {
    const handlePanStart = () => onCancel();
    window.addEventListener('flowchartpanstart', handlePanStart);
    return () => window.removeEventListener('flowchartpanstart', handlePanStart);
  }, [onCancel]);

  // Clamp to viewport
  const menuWidth = 248;
  const menuHeight = 150;
  let left = position.x - menuWidth / 2;
  let top = position.y + 16;

  if (left + menuWidth > window.innerWidth - 10) left = window.innerWidth - menuWidth - 10;
  if (left < 10) left = 10;
  if (top + menuHeight > window.innerHeight - 10) top = position.y - menuHeight - 16;
  if (top < 10) top = 10;

  const menu = (
    <div
      ref={menuRef}
      className="bridge-delete-menu"
      style={{ position: 'fixed', left, top, zIndex: 10000 }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="bridge-delete-menu__header">
        <span className="bridge-delete-menu__title">
          {taskCount === 1 ? 'This node has connections' : 'These nodes have connections'}
        </span>
        <span className="bridge-delete-menu__subtitle">
          {upstreamCount} upstream · {downstreamCount} downstream
        </span>
      </div>

      <button className="bridge-delete-menu__item bridge-delete-menu__item--primary" onClick={onBridge}>
        <span className="bridge-delete-menu__item-icon">🔗</span>
        <span className="bridge-delete-menu__item-text">
          <span className="bridge-delete-menu__item-label">Delete &amp; bridge</span>
          <span className="bridge-delete-menu__item-desc">Connect upstream directly to downstream</span>
        </span>
        <span className="bridge-delete-menu__item-key">↵</span>
      </button>

      <button className="bridge-delete-menu__item" onClick={onBreak}>
        <span className="bridge-delete-menu__item-icon">✂️</span>
        <span className="bridge-delete-menu__item-text">
          <span className="bridge-delete-menu__item-label">Just delete</span>
          <span className="bridge-delete-menu__item-desc">Break the connections</span>
        </span>
        <span className="bridge-delete-menu__item-key">Del</span>
      </button>
    </div>
  );

  // Portal to escape canvas transform context
  return ReactDOM.createPortal(menu, document.body);
};

export default BridgeDeleteMenu;
